import { axiosSerializerobjectToQueryParams, ListOutput } from '@allocab/allocab-commons-shared-types';
import { DedicatedPackageOutput, FindDedicatedPackageInput } from '@allocab/allocab-dedicated-driver-types';
import { PackageStatus } from '@allocab/allocab-dedicated-driver-types/lib/src/dedicated-package/types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '../services/datastore/queryKeys';
import { DEFAULT_CACHE_TIME, queryClient } from '../services/datastore/react-query';
import { InfiniteQueryOptions } from '../services/datastore/types';
import { driverApi } from '../services/network/Api';

export const PACKAGES_LIMIT = 10;

export type Input = Omit<FindDedicatedPackageInput, 'offset' | 'limit' | 'status'> & {
  limit?: number;
  status: PackageStatus[];
};

export const fetchTripPackagesByDriverKey = (driverKey: string, input: FindDedicatedPackageInput) =>
  driverApi.get<FindDedicatedPackageInput, DedicatedPackageOutput, 'list'>(
    `/v1/dedicated-package/driver/${driverKey}`,
    input,
    {
      paramsSerializer: axiosSerializerobjectToQueryParams,
    },
  );
export const useFetchTripPackagesByDriverKey = (driverKey: string, input: Input, pageNumber = 1) => {
  const limit = input.limit ?? PACKAGES_LIMIT;
  const apiInput: FindDedicatedPackageInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  return useQuery(
    queryKeys.user.fetchInfiniteTripPackagesByDriverKey(driverKey, apiInput),
    () => fetchTripPackagesByDriverKey(driverKey, apiInput),
    {
      keepPreviousData: true,
    },
  );
};

export const prefetchTripPackagesPageByDriverKey = (driverKey: string, input: Input, pageNumber = 1) => {
  const limit = input.limit ?? PACKAGES_LIMIT;
  const apiInput: FindDedicatedPackageInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  queryClient.prefetchQuery(
    queryKeys.user.fetchTripPackagesByDriverKey(driverKey, apiInput),
    () => fetchTripPackagesByDriverKey(driverKey, apiInput),
    {
      staleTime: DEFAULT_CACHE_TIME,
    },
  );
};

export const useInfiniteFetchTripPackagesByDriverKey = (
  driverKey: string,
  input: Input,
  options: InfiniteQueryOptions<
    typeof queryKeys.user.fetchInfiniteTripPackagesByDriverKey,
    ListOutput<DedicatedPackageOutput>
  > = {},
) => {
  const fetchTripsQuery = useInfiniteQuery(
    queryKeys.user.fetchInfiniteTripPackagesByDriverKey(driverKey, input),
    ({ pageParam = 0 }: { pageParam?: number }) =>
      fetchTripPackagesByDriverKey(driverKey, { limit: PACKAGES_LIMIT, offset: pageParam, ...input }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.nbItems < PACKAGES_LIMIT ? undefined : PACKAGES_LIMIT * allPages.length,
      ...options,
    },
  );

  return fetchTripsQuery;
};
